import { BlockUI, AlertMessage, DialogMessage } from '../../Utility.jsx';
import React, { useState, useMemo, useEffect } from 'react';
import { RoleModel } from '../../../models/RoleModel.tsx';
import { useReadMutation, useCreateMutation, useUpdateMutation, useDeleteMutation  } from '../../../hooks/APIRequestHook.tsx';
import { BaseRequestAPIModel, BaseRequestComponentModel, RequestAPIUrlModel } from '../../../models/BaseRequestModel.tsx';
import { BaseResponseAPIModel } from '../../../models/BaseResponseModel.tsx';
import RoleFunctionTree from '../Master/RoleFunctionTree.tsx';
import { MST_ROLE_READ_URL, MST_ROLE_CREATE_URL, MST_ROLE_UPDATE_URL, MST_ROLE_DELETE_URL } from  '../../../const/APIUrl.js';
import { queryListUserCacheKey as cacheKey } from  '../../../const/QueryCacheKey.js';
import { formDataToPayload } from '../../../common/Object.tsx';
import { SUCCESSFULLY_SAVE, SUCCESSFULLY_DELETE } from  '../../../const/Message.js';
import { Dialog } from '@mui/material';

import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  useMaterialReactTable,
} from 'material-react-table';

import {
  TextField,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextareaAutosize,
  Box,
  Tooltip,
  IconButton,
} from '@mui/material';

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { setDelay } from  '../../../helpers/UI.js';

const DataTable = (request) => {
  const [ roleId, setRoleId ] = useState(null);
  const [ showDialogTree, setShowDialogTree ] = useState(false);  
  const { data : dataTable, isFetchReadLoading } = useReadMutation<RoleModel>({url : MST_ROLE_READ_URL, cacheKey, enableRefetch : request.enableRefetch});
  const { mutateAsync: deleteUser, isPending: isDeletingUser } = useDeleteMutation<RoleModel>({cacheKey});
  const [ showDialog, setShowDialog] = useState(false);
  const [ dialogSetting, setDialogSetting ]  = useState({showCloseButton : false, messages : [SUCCESSFULLY_SAVE]});

  const handleCloseDialog = () => {
    setShowDialog(false);
  };

  const handleDeleteUser = async (Id: number) => {      
      const request = new BaseRequestAPIModel<RoleModel>({ Id } as RoleModel, {} as any, { url: MST_ROLE_DELETE_URL } as RequestAPIUrlModel);
      let apiResponse = await deleteUser(request);      
      if (apiResponse.isSuccess !== undefined)
      {
            setShowDialog(true);
            if (!apiResponse.isSuccess) 
            {
                setDialogSetting({...dialogSetting, showCloseButton : true, messages : apiResponse.errorMessages });
            }
            else  {                                
                setDialogSetting({...dialogSetting, showCloseButton : false, messages : [SUCCESSFULLY_DELETE] });
                setDelay().then(() => {
                    setShowDialog(false);
                });
            }          
      }
  };  

  //DELETE action
  const openDeleteConfirmModal = (row: MRT_Row<RoleModel>) => {
    if (window.confirm('Are you sure you want to proceed?')) {
      handleDeleteUser(row.original.Id);
    }
  };  

  const columns = useMemo<MRT_ColumnDef<RoleModel>[]>(
    () => [
        { accessorKey: 'Id', header: 'Id', visibleInShowHideMenu: false },        
        { accessorKey: 'RoleName', header: 'RoleName', accessorFn: (row) => row.RoleName ?? '' },        
        { accessorKey: 'Notes', header: 'Notes', accessorFn: (row) => row.Notes?.substring(0, 30) ?? '' },
    ],
    [],
  );
  
  const table = useMaterialReactTable({
    columns,
    data: dataTable,
    enableRowActions: true, 
    createDisplayMode: 'modal',
    editDisplayMode:  'modal',
    enableEditing: true,
    state: {
      isLoading: isFetchReadLoading,
      showProgressBars: isFetchReadLoading,
    },
    initialState: {     
      showGlobalFilter: true,
       columnVisibility: {
          Id: false, 
          RoleId:false,
          Password:false,
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box>
        {request.pageFunction.create &&
          <Button
            startIcon={<AddCircleIcon />}
            variant="contained"
            onClick={() => {
              table.setCreatingRow(true);
            }}
          >
            Add
          </Button>
        }
      </Box>
    ),
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: 'flex', gap: '1rem' }}>
        {request.pageFunction.update &&
          <Tooltip title="Edit">
            <IconButton onClick={() => table.setEditingRow(row)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        }
        {request.pageFunction.delete &&
            <Tooltip title="Delete">
              <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
        }
        {request.pageFunction.roleFunctionMapping &&
          <Tooltip title="RoleFunction">
            <IconButton onClick={() => {
                  setShowDialogTree(true); 
                  setRoleId(row.original.Id);
              }}>
              <AccountTreeIcon />
            </IconButton>
          </Tooltip>
        }
      </Box>
    ),
    renderEditRowDialogContent: ({ row, table }) => (     
        <DialogContentModal row={row} table={table} title={"Edit"} />
    ),
    renderCreateRowDialogContent: ({ row, table }) => (
        <DialogContentModal row={row} table={table} title={"New"} /> 
    ),
  });

  return(
    <>
      { isDeletingUser && <BlockUI /> }
      { showDialog && <DialogMessage dialogSetting={dialogSetting} onClose={handleCloseDialog} />}
      { showDialogTree && <RoleFunctionTree roleId={roleId} isOpen={showDialogTree} handleClose={() => { setShowDialogTree(false); setRoleId(null);}} /> }
      <MaterialReactTable table={table} />
    </>
  );
};

const DialogContentModal = ({ title, row, table }) => {
  const [apiResponse, setApiResponse] = useState({} as BaseResponseAPIModel<RoleModel>);
  const [showDialog, setShowDialog] = useState(false);

  const { mutateAsync: createRole, isPending: isCreating } = useCreateMutation<RoleModel>({cacheKey});
  const { mutateAsync: updateRole, isPending: isUpdating } = useUpdateMutation<RoleModel>({cacheKey});

  //Create action
  const handleCreateUser = async (data: RoleModel) => { 
      const request = new BaseRequestAPIModel<RoleModel>(data, {} as any, { url: MST_ROLE_CREATE_URL, additionalUrl : MST_ROLE_READ_URL } as RequestAPIUrlModel);
      let response = await createRole(request);  
      setApiResponse(response);
      if (response.isSuccess) {        
        setShowDialog(!showDialog);
        setDelay().then(() => {            
            table.setCreatingRow(null);
        });
      }
  };

  //Update action
  const handleUpdateUser = async (data: RoleModel, updatedRow : RoleModel) => {
    const request = new BaseRequestAPIModel<RoleModel>(data, updatedRow, { url: MST_ROLE_UPDATE_URL } as RequestAPIUrlModel);
    let response = await updateRole(request);
    setApiResponse(response);    
    if (response.isSuccess) {
        setShowDialog(!showDialog);
        setDelay().then(() => {    
            table.setEditingRow(null);
        });
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const inputData = Object.fromEntries(formData.entries());

    let requestData = (formDataToPayload(inputData, row, table) as unknown) as any;
    
    if (requestData.payload.Id !== undefined && requestData.payload.Id > 0)
    {
      handleUpdateUser(requestData.payload, requestData.updatedRow);
    }
    else
    {
      handleCreateUser(requestData.payload);
    }
  };

  return(
    <Dialog open={true} className="dialog-overflow-hidden p-1 mui-dialog-custom">
      {(isCreating || isUpdating) && <BlockUI />}
      {showDialog && <DialogMessage />}
      <form onSubmit={handleFormSubmit} className="row p-3">
          <h1 className="h1">{title}</h1>
          <div className="form-group mb-3">
            {<AlertMessage type={"error"} messages={apiResponse.errorMessages} />}
          </div>
          <TextField name="Id" label="Id" variant="standard" defaultValue={row.original.Id} sx={{ display: { xs: 'none', md: 'none' }}}/>                      
          <div className="form-group mb-3">
            <TextField name="RoleName" className="col-md-12 col-12" label="Role Name" variant="standard" defaultValue={row.original.RoleName} />
          </div>
          <div className="form-group mb-3">
          <TextareaAutosize
            className="col-md-12 col-12"
            name="Notes"
            aria-label=""
            minRows={4}
            placeholder="Notes"
            defaultValue={row.original.Notes}
          />
          </div>
          <div className="form-group text-end">
            <Button className="col-lg-2 col-md-12 col-12"
              onClick={() => {
                table.setCreatingRow(null);                
                if (row.original.Id > 0){
                  table.setEditingRow(null);
                } 
              }}
            >
              Cancel
            </Button>
            <Button className="col-lg-2 col-md-12 col-12" type="submit" variant="contained" disabled={isCreating || isUpdating}>Save</Button>
          </div>
        </form>
    </Dialog>
  );
};

const MstRoleComponent: React.FC<BaseRequestComponentModel> = ({pageFunction}) => {
  const [enableRefetch , setEnableRefetch] = useState(true);
  
  useEffect(() => {
    setEnableRefetch(false);
  },[enableRefetch]);

  return (
    <>        
      <DataTable enableRefetch={enableRefetch} pageFunction={pageFunction}/>
    </>
  );
};

export default MstRoleComponent;